import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import { SortableHandle } from "react-sortable-hoc";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { StyledSwapArrow } from "../../displayManager/styles";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import { useProperty } from "../../../components/property-provider";
import { defaultCrudMessages } from "../../shared/crud/simple-crud";
import CrudList, { TableColumns } from "../../shared/crud/list";
import { StringRenderer } from "../../shared/renderers/table";
import {
  DefaultTableRowActionsProps,
  Delete,
  Edit,
  StyledReactTooltip,
} from "../../shared/actions/tableActions";
import { GlobalActionsConfigEntity } from "./domain/globalActionConfig.entity";
import { GlobalActionsConfigGrpcRepository } from "./infrastructure/repositories/GlobalActionsConfigGrpcRepository";
import { useGlobalActionsConfigs } from "./application/hooks/useGlobalActionsConfigs";
import { useGlobalActionsConfigMutations } from "./application/hooks/useGlobalActionsConfigMutations";
import SwitchComponent from "../../witadmin/CRUD/Common/SwitchGroup";
import { SwitchWrapper } from "../../taxation";
import {
  createActionsSummaryRenderer,
  createConditionsSummaryRenderer,
} from "../shared/components/summaryRenderers";
import { messages as globalActionMessages } from "./globalAction.messages";

const globalActionsRepository = new GlobalActionsConfigGrpcRepository();

const messages = {
  ...defaultCrudMessages,
  ...globalActionMessages,
  entityName: {
    id: "orchestrator.globalActions.entityName",
    defaultMessage: "Global actions",
  },
  tableHeaders: defineMessages({
    active: {
      id: "default.active",
      defaultMessage: "Active",
    },
    ticker: {
      id: "app.ticker",
      defaultMessage: "Ticker",
    },
    criteria: {
      id: "orchestrator.globalActions.header.criteria",
      defaultMessage: "Conditions",
    },
    actions: {
      id: "orchestrator.globalActions.header.actions",
      defaultMessage: "Actions",
    },
  }),
};

function GlobalAction() {
  const intl = useIntl();
  const history = useHistory();
  const propertyInfo = useProperty();
  const { refetch } = useGlobalActionsConfigs(propertyInfo.ticker, globalActionsRepository, {
    enabled: false,
  });
  const { deleteConfig, reorderConfig } = useGlobalActionsConfigMutations(
    propertyInfo.ticker,
    globalActionsRepository
  );

  const tableColumns: TableColumns<GlobalActionsConfigEntity> = useMemo(
    () => ({
      active: {
        renderer: props => (
          <SwitchWrapper>
            <SwitchComponent
              value={props.value ?? false}
              field={{ className: "witadmin-bs3" }}
              caller=""
              entity={{}}
            />
          </SwitchWrapper>
        ),
        width: 100,
      },
      ticker: {
        renderer: StringRenderer,
        width: 200,
      },
      criteria: {
        renderer: createConditionsSummaryRenderer(intl, messages),
      },
      actions: {
        renderer: createActionsSummaryRenderer(intl, messages),
      },
    }),
    [intl]
  );

  const handleGetAll = useCallback(async () => {
    const result = await refetch();
    return result.data ?? [];
  }, [refetch]);

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteConfig(id);
    },
    [deleteConfig]
  );

  const handleReorder = useCallback(
    async (id: string, afterId: string | undefined) => {
      await reorderConfig({ id, after: afterId ?? "" });
    },
    [reorderConfig]
  );

  const handleGoToEdit = useCallback((id: string) => history.push(`actions/edit/${id}`), [history]);

  const DragHandle = useMemo(
    () =>
      SortableHandle(() => (
        <Button
          data-tip="React-tooltip"
          data-for="drag"
          size="sm"
          variant="light"
          className="mr-1"
          style={{ cursor: "move" }}>
          <StyledSwapArrow />
        </Button>
      )),
    []
  );

  const RowActions = useMemo(
    () => ({ row, rest, onReloadList }: DefaultTableRowActionsProps<GlobalActionsConfigEntity>) => {
      return (
        <Table.Cell {...rest} style={{ zIndex: 999 }}>
          <StyledReactTooltip id="drag" effect="solid">
            <FormattedMessage {...messages.drag} />
          </StyledReactTooltip>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...messages.edit} />
          </StyledReactTooltip>

          <DragHandle />

          <Edit row={row} onGoToEdit={handleGoToEdit} />

          <Delete
            rest={rest}
            row={row}
            onDelete={handleDelete}
            onReloadList={onReloadList}
            useOverlay
          />
        </Table.Cell>
      );
    },
    [DragHandle, handleGoToEdit, handleDelete]
  );

  const TableActions = useCallback(
    () => (
      <div className="flex gap-4">
        <Link className="btn btn-primary" to="actions/new">
          <FormattedMessage {...messages.create} />
        </Link>
      </div>
    ),
    []
  );

  return (
    <CrudList<GlobalActionsConfigEntity>
      columns={tableColumns}
      onGetAll={handleGetAll}
      onReorder={handleReorder}
      actions={TableActions}
      rowActions={RowActions}
      forceLoad
      messages={messages}
      hideExportButton
    />
  );
}

export default withAuthorizationVerifier(GlobalAction);
